import { Link } from '@tanstack/react-router';
import { ArrowLeft, ChevronRight, Megaphone, Flag, Eye, Users } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  useAdminChallenges,
  type AdminChallengeRow,
} from '@/features/challenge-reel/use-challenge-reel';

/** Les demandes de page d'accueil à modérer d'abord, puis les plus récents. PUR. */
export function sortChallengesForReview(list: readonly AdminChallengeRow[]): AdminChallengeRow[] {
  return [...list].sort(
    (a, b) =>
      Number(b.public_request === 'pending') - Number(a.public_request === 'pending') ||
      b.created_at.localeCompare(a.created_at),
  );
}

export function TutorAdminChallengesPage() {
  const { data, isLoading } = useAdminChallenges();
  const challenges = data ? sortChallengesForReview(data) : undefined;
  const pending = (challenges ?? []).filter((c) => c.public_request === 'pending').length;

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link
        to="/tutor/dashboard"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Admin
      </Link>

      <div className="mb-6">
        <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
          Défis sociaux
        </p>
        <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight">
          Diffuser un défi
        </h1>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Choisis un défi à publier en YouTube Short / TikTok. {challenges?.length ?? 0} défi
          {(challenges?.length ?? 0) !== 1 ? 's' : ''}.
          {pending > 0 && (
            <strong className="text-accent-600">
              {' '}
              {pending} demande{pending > 1 ? 's' : ''} de page d’accueil à modérer.
            </strong>
          )}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-20 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (challenges?.length ?? 0) === 0 ? (
        <div className="border-ink-200 flex flex-col items-center rounded-2xl border border-dashed bg-white p-10 text-center">
          <Megaphone className="text-ink-300 h-8 w-8" />
          <p className="font-display text-ink-700 mt-3 text-sm font-bold">Aucun défi</p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Les défis créés par les élèves apparaîtront ici.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(challenges ?? []).map((c) => (
            <ChallengeRow key={c.id} challenge={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChallengeRow({ challenge: c }: { challenge: AdminChallengeRow }) {
  const successRate =
    c.total_attempts >= 20 ? Math.round((c.total_success / c.total_attempts) * 100) : null;
  const thumb = c.thumbnail_url ?? (c.media_type === 'photo' ? c.media_url : null);

  return (
    <Link
      to="/tutor/admin/challenges/$challengeId"
      params={{ challengeId: c.id }}
      className="border-ink-200 hover:border-primary-200 flex items-center gap-3 rounded-2xl border bg-white p-3 transition-all hover:shadow-md active:scale-[.99]"
    >
      <div className="bg-ink-100 relative h-16 w-12 shrink-0 overflow-hidden rounded-lg">
        {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          {c.is_flagged && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">
              <Flag className="h-3 w-3" /> Signalé
            </span>
          )}
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-bold',
              c.is_public ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500',
            )}
          >
            {c.is_public ? 'Public' : 'Privé'}
          </span>
          {c.public_request === 'pending' && (
            <span className="bg-accent-100 text-accent-700 rounded-full px-2 py-0.5 text-[11px] font-bold">
              À modérer
            </span>
          )}
          {c.level && (
            <span className="bg-primary-100 text-primary-700 rounded-full px-2 py-0.5 text-[11px] font-bold">
              {c.level}
            </span>
          )}
        </div>
        <h3 className="font-display text-ink-900 line-clamp-1 text-[15px] font-extrabold">
          {c.title}
        </h3>
        <p className="text-ink-500 line-clamp-1 font-sans text-xs">{c.exercise_title}</p>
        <div className="text-ink-400 mt-1 flex items-center gap-3 font-sans text-[11px]">
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" /> {c.total_views}
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3" /> {c.total_attempts}
          </span>
          {successRate !== null && <span>🎯 {successRate}%</span>}
        </div>
      </div>

      <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" />
    </Link>
  );
}
